<template>
  <div class="player-info-wrapper">
    <div class="left">
      <template v-if="smallCardShow">
        <div class="label">
          <player-material
            :feed="feed"
            @on-click-material="onClickMaterial"
            @show-player-material="showPlayerMaterial"
          />
          <small-card
            :is-show-material="isShowMaterial"
            :display-info="displayInfo"
            @on-click-card="smallCardClick"
          />
        </div>
        <player-poster
          v-show="isNewUI || !showCollection"
          :feed="posterFeed"
          :show-focus="!isAdFeed"
          :mini-app-path-click-follow="miniAppPathClickFollow"
          :mini-app-path-click-person="miniAppPathClickPerson"
          @on-click-poster="onClickPoster"
        />
        <player-info-txt
          :feed="feed"
          @on-click-info="onClickInfo"
        />
        <player-label
          :feed="feed"
          :animate="!showCollection && isActive"
          @on-click-label="onClickLabel"
        />
      </template>
      <template v-else>
        <big-card
          :goods-info="goodsInfo"
          :display-info="displayInfo"
          @on-click-card="bigCardClick"
          @close="closeBigCard"
        />
      </template>
    </div>
    <div class="right">
      <player-control
        v-show="isNewUI || !showCollection"
        :feed="feed"
        :layout="layout"
        :hide-fake-like-heart="true"
        :collection-keyword="collectionKeyword"
        :mini-app-path-click-comment="miniAppPathClickComment"
        :mini-app-path-click-like="miniAppPathClickLike"
        @on-click-control="onClickControl"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { inject, toRefs, ref, computed, onMounted, watch } from 'vue';
import { useStore } from '@/store/index';
import { PROVIDED_COOKIE_INFO_NAME, Cookieinfo } from '@/composable/use-provide-cookie-info';
import useGetCommonMethods from '@/composable/use-get-common-method';
import useProvideUrlInfo from '@/composable/use-provide-url-info';
import useProvideBrowserInfo from '@/composable/use-provide-browser-info';
import { PlayerInfoReport } from '@/utils/player-info';
import { IS_SSR } from '@/utils/env';
import { changeShareMaskText, TEXT } from '@/utils/share';
import { getSchemaFromExposureMaterial, isWeiShiSchema, appendVersionInfoToSchema } from '@/utils/schema';
import { EMiniAppPosition } from '@/constants/mini-app';
import { EPosterTag, EControlTag, ELabelTag, EInfoTag } from '@/constants/player-info';
import fetchNativeAd from '@/services/fetch-native-ad';
import { ModelMetaFeed, ModelAdInfo, ModelGoodsInfo, ModelGoodsDisplayInfo } from '@/types/Model';
import { IHashMap } from '@/types/data';
import { EFeedReserveType } from '@/constants/feed';
import { report } from '@/libs/report';
import PlayerMaterial from './common/player-material.vue';
import PlayerPoster from './common/player-poster.vue';
import PlayerControl from './common/player-control.vue';
import PlayerInfoTxt from './common/player-info-txt.vue';
import PlayerLabel from './common/player-label.vue';
import BigCard from './common/big-card.vue';
import SmallCard from './common/small-card.vue';

const { info: urlInfo } = useProvideUrlInfo();
const { info: browserInfo } = useProvideBrowserInfo();
const { spid = '', channel, collectionid: collectionId } = urlInfo.query || {};
// 复制链接过来的且处于微信中去掉表态按钮
const isWxFromLink = !channel && browserInfo.client === 'wx';
const baseLayout = isWxFromLink ? 'like,comment,shareText' : 'rich,like,comment,shareText';

interface Props {
  feed: ModelMetaFeed,
  isPlay: boolean,
  currentTime: number,
}
// defineEmits里面不能使用局部变量
const emit = defineEmits(['on-click-collection']);

// props
const props = withDefaults(defineProps<Props>(), {
  feed: () => ({} as unknown as ModelMetaFeed),
  isPlay: false,
  currentTime: 0,
});
const {
  feed,
  isPlay,
  currentTime,
} = toRefs(props);

// data
const smallCardShow = ref(true);
const isBigCardShow = ref(false);
const goodsInfo = ref<ModelGoodsInfo>();
const displayInfo = ref<ModelGoodsDisplayInfo>();
const cardShowTime = ref(5000);
const isShowMaterial = ref(false);
const adInfo = ref<ModelAdInfo>();
const timer = ref(-999);

// composable
const cookieInfo = inject(PROVIDED_COOKIE_INFO_NAME) as Cookieinfo;
const { getMiniAppPath, openSchemaCheckInstallInQQAppendParams, getLogsour } = useGetCommonMethods();

// state
const { state } = useStore();
const showCollection = computed(() => state.showCollection);
const shareUi = computed(() => state.shareUi);
const isNewUI = computed(() => state.isNewUI);
const growthTestData = computed(() => state.growthTestData);
const activeIndex = computed(() => state.activeIndex);
const isCollection = computed(() => state.isCollection);
const collectionInfo = computed(() => state.collectionInfo);
const isFullScreen = computed(() => state.isFullScreen);
const isWsInstalled = computed(() => state.isWsInstalled);

// computed
const exposureMaterialInfo = computed(() => feed.value.reserve?.[EFeedReserveType.exposureMaterialInfo] || {});
const isActive = computed(() => feed.value.index === activeIndex.value);
const isSupportExposureMaterial = computed(() => {
  const { exposureMaterialName, materialId } = exposureMaterialInfo.value;
  return !!(exposureMaterialName || materialId);
});
const topicId = computed(() => {
  const { videoConfig, topicId } = feed.value;
  return videoConfig?.tid || videoConfig?.tid || topicId;
});
const playerInfoReport = computed(() => new PlayerInfoReport({
  isSupportExposureMaterial: isSupportExposureMaterial.value,
  growthTestData: growthTestData.value,
  feed: feed.value,
  topicId: topicId.value,
}));
const collectionKeyword = computed(() => (isCollection.value ? collectionInfo.value?.keyWord : ''));
const layout = computed(() => (isCollection.value && isNewUI.value ? `collection,${baseLayout}` : baseLayout));
const isAdFeed = computed(() => !!feed.value.reserve?.[EFeedReserveType.business]);
const userPid = cookieInfo.person_id || '';
const posterFeed = computed(() => (isAdFeed.value
  ? {
    ...feed.value,
    poster: {
      ...feed.value.poster,
      avatar: 'https://isee.weishi.qq.com/daguo/img/ws-ad.png',
      nick: '微视广告君',
    },
  }
  : feed.value
));
const miniAppPathClickLike = getMiniAppPath(EMiniAppPosition.videoLike);
const miniAppPathClickComment = getMiniAppPath(EMiniAppPosition.videoComment);
const miniAppPathClickFollow = computed(() => getMiniAppPath(EMiniAppPosition.videoHeadpicFocus, {
  ownerid: feed.value?.poster?.id || '',
}));
const miniAppPathClickPerson = computed(() => getMiniAppPath(EMiniAppPosition.videoHeadpic, {
  ownerid: feed.value.poster.id || '',
}));

// methods
const openFeedSchema = () => {
  const logsour = getLogsour();
  openSchemaCheckInstallInQQAppendParams(true, isCollection.value, `weishi://feed?feed_id=${feed.value.id}&session_from=10000018${logsour}`);
};
const openPersonalSchema = () => {
  const logsour = getLogsour();
  openSchemaCheckInstallInQQAppendParams(true, isCollection.value, `weishi://profile?person_id=${feed.value.poster.id}${logsour}`);
};

const onClickInfo = (tag: EInfoTag, params: IHashMap) => {
  if (isWxFromLink) {
    changeShareMaskText(TEXT.OPEN);
    shareUi.value?.open();
    return;
  }

  const logsour = getLogsour();
  switch (tag) {
    case EInfoTag.topic:
      playerInfoReport.value.report('video.topic.click', {
        type: { topic_id: topicId.value },
      });
      openSchemaCheckInstallInQQAppendParams(
        true,
        isCollection.value,
        `weishi://topic?topic_id=${topicId.value}&session_from=10000013${logsour}`,
      );
      break;
    case EInfoTag.person:
      openSchemaCheckInstallInQQAppendParams(true, isCollection.value, `weishi://profile?person_id=${params?.uid}${logsour}`);
      break;
    case EInfoTag.desc:
      openFeedSchema();
      break;
    default:
      openPersonalSchema();
      break;
  }
};

const onClickLabel = (tag: ELabelTag) => {
  if (isWxFromLink) {
    changeShareMaskText(TEXT.OPEN);
    shareUi.value?.open();
    return;
  }

  const logsour = getLogsour();
  switch (tag) {
    case ELabelTag.geo:
      playerInfoReport.value.report('video.location.click');
      openSchemaCheckInstallInQQAppendParams(true, isCollection.value, `weishi://geocollec?geo_id=${feed.value.geoInfo.polyGeoID}${logsour}`);
      break;
    default:
      openFeedSchema();
      break;
  }
};

const onClickPoster = (tag: EPosterTag, clickMiniApp: boolean) => {
  if (isWxFromLink) {
    changeShareMaskText(TEXT.OPEN);
    shareUi.value?.open();
    return;
  }
  const onClickAvatarOrNick = () => {
    if (!clickMiniApp) {
      if (isAdFeed.value) {
        openFeedSchema();
      } else {
        openPersonalSchema();
      }
    }
  };
  switch (tag) {
    case EPosterTag.posterAvatar:
      playerInfoReport.value.reportWithFeedCommonOpt('video.headpic.click', EMiniAppPosition.videoHeadpic, {
        is_pull_pos: isWsInstalled.value ? 1 : 2,
      });
      appPopupReport('video.headpic');

      onClickAvatarOrNick();
      break;
    case EPosterTag.posterNick:
      playerInfoReport.value.reportWithFeedCommonOpt('video.nickname.click', null, {
        is_pull_pos: isWsInstalled.value ? 1 : 2,
      });
      appPopupReport('video.nickname');

      onClickAvatarOrNick();
      break;
    case EPosterTag.posterFollow:
      playerInfoReport.value.reportWithFeedCommonOpt('video.headpic.focus.click', EMiniAppPosition.videoHeadpic, {
        is_pull_pos: isWsInstalled.value ? 1 : 2,
      });

      if (!clickMiniApp) {
        appPopupReport('video.headpic.focus');
        openPersonalSchema();
      }
      break;
    default:
      if (!clickMiniApp) {
        openFeedSchema();
      }
  }
};
const onClickMaterial = (tempSchema: string) => {
  if (isWxFromLink) {
    changeShareMaskText(TEXT.OPEN);
    shareUi.value?.open();
    return;
  }
  let schema;
  if (isWeiShiSchema(tempSchema)) {
    schema = `${tempSchema.replace('&music_id=${music_id}', '')}&upload_from=15`;
    schema = appendVersionInfoToSchema(schema, exposureMaterialInfo.value);
  } else {
    schema = getSchemaFromExposureMaterial(exposureMaterialInfo.value);
  }
  const logsour = getLogsour();
  schema = `schema${logsour}`;

  playerInfoReport.value.report('video.cameratoo.click', playerInfoReport.value.exposureMaterialReportOpt());
  openSchemaCheckInstallInQQAppendParams(true, isCollection.value, schema);
};
const showPlayerMaterial = (isShow: boolean) => {
  isShowMaterial.value = isShow;
};
const onClickControl = (tag: EControlTag, clickMiniApp: boolean) => {
  if (isWxFromLink) {
    changeShareMaskText(TEXT.OPEN);
    shareUi.value?.open();
    return;
  }
  const openSchema = () => {
    if (!clickMiniApp) {
      openFeedSchema();
    }
  };
  switch (tag) {
    case EControlTag.collectionIcon:
      emit('on-click-collection');
      break;
    case EControlTag.likeIcon:
      playerInfoReport.value.reportLike('click', {
        is_pull_pos: isWsInstalled.value ? 1 : 2,
      });
      appPopupReport('video.like');

      openSchema();
      break;
    case EControlTag.commentIcon:
      playerInfoReport.value.reportWithFeedCommonOpt('video.comment.click', EMiniAppPosition.videoComment, {
        is_pull_pos: isWsInstalled.value ? 1 : 2,
      });
      appPopupReport('video.comment');

      openSchema();
      break;
    case EControlTag.shareIcon:
      shareUi.value?.open();
      playerInfoReport.value.reportWithFeedCommonOpt('video.share.click', EMiniAppPosition.videoComment);
      break;
    default:
      openSchema();
  }
};

const appPopupReport = (pos: string) => {
  report('call.app.popup.exposure', {
    type: {
      micro_drama_id: '',
      play_status: isFullScreen.value ? 2 : 1,
      clk_position: pos,
    },
  });
};

// 商品卡上报
const cardReport = (typeData: string) => {
  if (!adInfo.value) return;
  playerInfoReport.value.cardReport(typeData, {
    adInfo: adInfo.value,
    currentTime: currentTime.value,
  });
};
const smallCardClick = () => {
  cardReport('video.businesscard.shopping.short.click');
  openFeedSchema();
};
const bigCardClick = () => {
  cardReport('video.businesscard.shopping.long.click');
  openFeedSchema();
};
const closeBigCard = () => {
  cardReport('video.businesscard.shopping.long.close.click');
  smallCardShow.value = true;
};
const cardShowTimeout = () => {
  if (isPlay.value && !isBigCardShow.value) {
    isBigCardShow.value = true;
    clearTimeout(timer.value);
    timer.value = window.setTimeout(() => {
      smallCardShow.value = false;
      cardReport('video.businesscard.shopping.long.exposure');
    }, cardShowTime.value);
  } else {
    clearTimeout(timer.value);
  }
};
const getCommercialData = async () => {
  const feedId = feed.value?.id;
  const personId = userPid;
  const timestamp = new Date().getTime();
  const random = Math.random() * 10e+3;
  const adTraceId = `native_${feedId}_${personId}_${timestamp}_${random.toFixed(0)}`;
  const res = await fetchNativeAd({
    ad_trace_id: adTraceId,
    source: 6, // 端外 分享页请求原生广告
    session_id: adTraceId,
    pid: personId,
    feed_id: feedId,
    publisher_pid: feed.value?.poster?.id,
  });
  if (res.length) {
    const resAdInfo = res[0];
    adInfo.value = resAdInfo;
    displayInfo.value = resAdInfo.goodsMaterialInfo?.displayInfo;
    goodsInfo.value = resAdInfo.goodsMaterialInfo?.goodsInfo;
    // 小卡曝光
    if (displayInfo.value?.buttonTxt) {
      cardReport('video.businesscard.shopping.short.exposure');
    }
    cardShowTimeout();
  }
};
// 旧精选集 UI 切换视频初始化参数
const initData = () => {
  clearTimeout(timer.value);
  smallCardShow.value = true;
  isBigCardShow.value = false; // 大卡是否已经显示过
  goodsInfo.value = undefined; // 商业化信息
  displayInfo.value = undefined; // 标签、标题、按钮之类文案
  isShowMaterial.value = false; // 小卡前是否有拍同款等标签
};

onMounted(() => {
  // playerInfoReport.value.mountedReport();
});
watch(isPlay, () => {
  if (IS_SSR || !goodsInfo.value) {
    return;
  }
  cardShowTimeout();
});
// 如果activeIndex有更新
watch(activeIndex, () => {
  if (isActive.value) {
    playerInfoReport.value.mountedReport();
  }
  if (IS_SSR || !spid) return;
  // 如果是旧的精选集
  if (!isNewUI.value && isCollection.value) {
    initData();
    getCommercialData();
  } else {  // 新的精选集或者普通视频
    if (isActive.value) {
      getCommercialData();
    }
  }
}, { immediate: true });
</script>

<style lang="scss" scoped>
.player-info-wrapper {
  display: flex;
  align-items: flex-end;
  @include responsiveStyleSingle(padding-left, 2.6667, 'vw');
  @include responsiveStyleSingle(padding-right, 2.6667, 'vw');
  @include responsiveStyleSingle(padding-bottom, 10, 'vw');

  .left {
    flex: 1;
    @include responsiveStyleSingle(margin-right, 1, 'vw');
    overflow: hidden;
    .label {
      display: flex;
      @include responsiveStyleSingle(margin-bottom, 2.6667, 'vw');
    }
  }
  .right {
    @include responsiveStyleSingle(width, 9.6, 'vw');
  }
  .left,
  .right {
    pointer-events: auto;
  }
}
</style>
